/*
Global file for setting React routes.
*/

import React from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';

// Import view components for this flow, sort of like different views or page content
import Pricing from '~/views/Pricing';
import Numbers from '~/views/Numbers';
import LocalRecommend from '~/views/LocalRecommend';
import LocalSearch from '~/views/LocalSearch';
import TollFree from '~/views/TollFree';
import CustomTollFree from '~/views/CustomTollFree';
import TransferCurrentNumber from '~/views/TransferCurrentNumber';
import ConfirmNumberTransfer from '~/views/ConfirmNumberTransfer';
import Billing from '~/views/Billing';
import Pending from '~/views/Pending';
import TrialSignup from '~/views/TrialSignup';
import ExpiredTrialBlocker from '~/views/ExpiredTrialBlocker';
import Ruby from '~/views/Ruby';
import SignedIn from '~/views/SignedIn';
import AnnualUpgrade from '~/views/AnnualUpgrade';
import SignupSuccess from '~/views/SignupSuccess';

// import stores to verify route permissions
import { lockNumberStore } from '~/common/stores/domain/lockNumber.js';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite.js';
import { pendingStore } from '~/common/stores/domain/pending.js';
import { convertToPaidStore } from '~/common/stores/domain/convertToPaid.js';
import { transferNumberStore } from '~/common/stores/domain/transferNumber.js';
import { trialSignupStore } from '~/common/stores/domain/trialSignup.js';

import {
    PATH_PLANS, PATH_NUMBERS, PATH_LOCAL_RECOMMEND, PATH_LOCAL_SEARCH, PATH_TOLL_FREE, PATH_CUSTOM_TOLL_FREE, PATH_TRANSFER_CURRENT_NUMBER, PATH_CONFIRM_NUMBER_TRANSFER,
    PATH_BILLING, PATH_PENDING, PATH_ANNUAL_UPGRADE,
    PATH_TRIAL_NUMBERS, PATH_TRIAL_SIGNUP, PATH_TRIAL_SIGNUP_SUCCESS,
    PATH_TRIAL_LOGIN, PATH_TRIAL_UPGRADE,
    PATH_EXPIRED_TRIAL_BLOCKER, PATH_RUBY,
    PATH_ORDER_CONFIRMATION, PATH_SIGNED_IN,
    PATH_SIGNUP_SUCCESS
} from '~/common/utils/consts.js';
// import { directPurchaseStore } from './common/stores/ui/directPurchase';

const Routes = () => (
    <Switch> {/* Switch, Renders first <Route> or <Redirect> that matches the location. */}
        <Route
            component={Pricing}
            path={PATH_PLANS}
        />
        <Route
            component={Numbers}
            exact
            path={PATH_NUMBERS}
        />
        {/* Vanity number views*/}
        <Route
            component={TollFree}
            path={PATH_TOLL_FREE}

        />
        <Route
            component={CustomTollFree}
            path={PATH_CUSTOM_TOLL_FREE}
        />
        {/* Local number views*/}
        <Route
            component={LocalRecommend}
            path={PATH_LOCAL_RECOMMEND}
        />
        <Route
            component={LocalSearch}
            path={PATH_LOCAL_SEARCH}
        />
        {/* Transfer number views*/}
        <Route
            component={TransferCurrentNumber}
            path={PATH_TRANSFER_CURRENT_NUMBER}
        />
        <Route
            path={PATH_CONFIRM_NUMBER_TRANSFER}
            render={() => {
                if (transferNumberStore.transferNumberLength > 0) {
                    return <ConfirmNumberTransfer />;
                }
                return <Redirect to={PATH_TRANSFER_CURRENT_NUMBER} />;
            }}
        />
        {/* sign up for trial views*/}
        <Route
            component={ExpiredTrialBlocker}
            path={PATH_EXPIRED_TRIAL_BLOCKER}
        />
        <Route
            component={TrialSignup}
            path={PATH_TRIAL_NUMBERS}
        />
        <Route
            path={PATH_TRIAL_SIGNUP}
            render={() => {
                if (
                    lockNumberStore.numberIsSelected &&
                        lockNumberStore.numberIsLocked &&
                        !lockNumberStore.isSelectedAreaCodePremium
                ) {
                    return <TrialSignup />;
                }
                return <Redirect to={PATH_TRIAL_NUMBERS} />;
            }}
        />
        <Route
            path={PATH_TRIAL_SIGNUP_SUCCESS}
            render={() => (trialSignupStore.signupTrialSuccessful === true ?
                <SignupSuccess />
                :
                <Redirect to={PATH_TRIAL_NUMBERS} />)
            }
        />
        {/* convert to paid success */}
        <Route
            component={SignupSuccess}
            path={PATH_ORDER_CONFIRMATION}
            // render={() => (convertToPaidStore.trialConverted ? <SignupSuccess /> : <Redirect to={PATH_BILLING} />)}
        />
        {/* upgrade view */}
        <Route
            component={AnnualUpgrade}
            path={PATH_ANNUAL_UPGRADE}
        />
        {/* Billing and Pending views*/}
        <Route
            path={PATH_BILLING}
            render={() => {
                // change to correct url if user is converting trial to paid (should fall down to the next if block next time)
                if (
                    convertToPaidStore.isUserConvertingToPaid &&
                        location.pathname !== PATH_TRIAL_UPGRADE
                ) {
                    return <Redirect to={{ pathname: PATH_TRIAL_UPGRADE, search: location.search }} />;
                }

                // render only if user selected a plan and either picked a number or logged in with one (Convert to Paid flow)
                if (
                    offerSuiteStore.selectedOfferId &&
                        (convertToPaidStore.isUserConvertingToPaid || (lockNumberStore.numberIsSelected && lockNumberStore.numberIsLocked))
                ) {
                    return <Billing />;
                }

                // otherwise take them back to the Plans page
                return <Redirect to={{ pathname: PATH_PLANS, search: location.search }} />;
            }}
        />
        <Route
            path={PATH_PENDING}
            render={() => (pendingStore.referenceNumber && pendingStore.selectedNumber ?
                <Pending />
                :
                <Redirect to={PATH_PLANS} />)
            }
        />
        <Route
            component={Ruby}
            path={PATH_RUBY}
        />
        <Route
            component={SignedIn}
            path={PATH_SIGNED_IN}
        />
        <Route
            component={SignupSuccess}
            path={PATH_SIGNUP_SUCCESS}
            // render={() => (directPurchaseStore.accountCreated ? <SignupSuccess /> : <Redirect to={PATH_PLANS} />)}
        />
        {/* fall backs for legacy code*/}
        <Route
            path='/ConvertFreeTrial'
            render={() => <Redirect to={{ pathname: PATH_TRIAL_LOGIN, search: location.search }} />}
        />
        <Route
            path='/forceRedirect/:path'
            render={() => <Redirect to='/:path' />}
        />
        <Route
            path={`/:lang/:flow${PATH_PLANS}`}
            render={() => <Redirect to={{ pathname: PATH_PLANS, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_TOLL_FREE}`}
            render={() => <Redirect to={{ pathname: PATH_TOLL_FREE, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_CUSTOM_TOLL_FREE}`}
            render={() => <Redirect to={{ pathname: PATH_CUSTOM_TOLL_FREE, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_LOCAL_RECOMMEND}`}
            render={() => <Redirect to={{ pathname: PATH_LOCAL_RECOMMEND, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_LOCAL_SEARCH}`}
            render={() => <Redirect to={{ pathname: PATH_LOCAL_SEARCH, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_TRANSFER_CURRENT_NUMBER}`}
            render={() => <Redirect to={{ pathname: PATH_TRANSFER_CURRENT_NUMBER, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_CONFIRM_NUMBER_TRANSFER}`}
            render={() => <Redirect to={{ pathname: PATH_CONFIRM_NUMBER_TRANSFER, search: location.search }} />}
        />
        <Route
            path={`/:lang/:flow${PATH_NUMBERS}`}
            render={() => <Redirect to={{ pathname: PATH_NUMBERS, search: location.search }} />}
        />
        {/* If no match is found for this flow go to the default flow */}
        <Route
            render={() => <Redirect to={{ pathname: PATH_PLANS, search: location.search }} />}
        />
    </Switch>
);

export default Routes;
